import { Box, IconButton } from '@mui/material';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import ZoomInGlyph from '../icons/ZoomInGlyph';
import ZoomOutGlyph from '../icons/ZoomOutGlyph';
import { KEYBOARD_PAN_STEP, useImageZoomPan } from './useImageZoomPan';
import useStyles from './VisualizationZoomDialog.styles';

export type ZoomableImageProps = {
  /** HTTPS source of the image being previewed. */
  src: string;
  /** Accessible alt text for the image. */
  alt: string;
};

/**
 * The pannable, zoomable image surface plus its zoom controls.
 *
 * Remounted by the parent (via `key={src}`) whenever the previewed
 * image changes so each image starts fit-to-view — no reset effect
 * needed.
 */
function ZoomableImage({ src, alt }: ZoomableImageProps) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const {
    scale,
    offset,
    isZoomed,
    isDragging,
    canZoomIn,
    canZoomOut,
    containerRef,
    imageRef,
    zoomIn,
    zoomOut,
    panBy,
    reclamp,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  } = useImageZoomPan();

  // Keyboard panning: once magnified the container is focusable so
  // keyboard-only users can move around the image (pointer drag alone
  // would leave them stuck). An arrow key shifts the view by one step;
  // e.g. ArrowRight reveals content to the right, so the image moves left.
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!isZoomed) {
      return;
    }
    switch (event.key) {
      case 'ArrowRight':
        panBy(-KEYBOARD_PAN_STEP, 0);
        break;
      case 'ArrowLeft':
        panBy(KEYBOARD_PAN_STEP, 0);
        break;
      case 'ArrowDown':
        panBy(0, -KEYBOARD_PAN_STEP);
        break;
      case 'ArrowUp':
        panBy(0, KEYBOARD_PAN_STEP);
        break;
      default:
        return;
    }
    event.preventDefault();
  };
  const ariaLabel = isZoomed
    ? t('chat.visualization.zoom.panAriaLabel')
    : undefined;

  return (
    <Box className={classes.body}>
      <Box
        ref={containerRef}
        aria-label={ariaLabel}
        className={cx(
          classes.imageContainer,
          isZoomed && classes.imageContainerZoomed,
          isDragging && classes.imageContainerDragging
        )}
        onKeyDown={handleKeyDown}
        onPointerCancel={onPointerUp}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        tabIndex={isZoomed ? 0 : -1}
      >
        <img
          ref={imageRef}
          alt={alt}
          className={cx(classes.image, !isDragging && classes.imageAnimated)}
          decoding="async"
          draggable={false}
          onLoad={reclamp}
          referrerPolicy="no-referrer"
          src={src}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          }}
        />
      </Box>
      <Box className={classes.controls}>
        <IconButton
          aria-label={t('chat.visualization.zoom.outAriaLabel')}
          className={classes.zoomButton}
          data-testid="ai-sidekick-visualization-zoom-out"
          disabled={!canZoomOut}
          onClick={zoomOut}
        >
          <ZoomOutGlyph />
        </IconButton>
        <IconButton
          aria-label={t('chat.visualization.zoom.inAriaLabel')}
          className={cx(
            classes.zoomButton,
            isZoomed && classes.zoomButtonActive
          )}
          data-active={isZoomed}
          data-testid="ai-sidekick-visualization-zoom-in"
          disabled={!canZoomIn}
          onClick={zoomIn}
        >
          <ZoomInGlyph />
        </IconButton>
      </Box>
    </Box>
  );
}

export default ZoomableImage;
